// src/pages/MCQBrowse.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { motion } from "framer-motion";
import { HiAcademicCap, HiSearch, HiLightningBolt, HiBookOpen, HiClipboardList } from "react-icons/hi";
import { db } from "../components/firebase/firebase";
import { useAuth } from "../context/AuthContext";
import Loader from "../components/common/Loader";
import { SEO } from "../components/common/SEO";
import SaveButton from "../components/student/SaveButton";

const BOARD_LABELS = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };
const BOARD_COLORS = {
  state:       "bg-blue-50 text-blue-700 border-blue-100",
  cbse:        "bg-indigo-50 text-indigo-700 border-indigo-100",
  competitive: "bg-sky-50 text-sky-700 border-sky-100",
};

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };
const fadeUp  = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

function MathSymbol({ symbol, className, delay = 0, duration = 7 }) {
  return (
    <motion.span
      aria-hidden
      className={`absolute select-none pointer-events-none font-bold text-white/20 ${className}`}
      animate={{ y: [0, -16, 0], rotate: [0, 8, 0] }}
      transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
    >
      {symbol}
    </motion.span>
  );
}

const BOARD_LABELS_FULL = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };

export default function MCQBrowse() {
  const { role, profile } = useAuth();
  const isStudent = role === "student";

  const [tests, setTests]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [boardFilter, setBoard] = useState("all");
  const [gradeFilter, setGrade] = useState("all");
  const [search, setSearch]     = useState("");

  // Pre-set filters to the student's assigned class once profile is known.
  useEffect(() => {
    if (isStudent && profile?.board) setBoard(profile.board);
    if (isStudent && profile?.grade) setGrade(String(profile.grade));
  }, [isStudent, profile]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "mcqs"));
        setTests(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt ? new Date(b.createdAt) : 0) - (a.createdAt ? new Date(a.createdAt) : 0)));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const grades = [...new Set(tests.map((t) => t.grade))].sort((a, b) => Number(a) - Number(b));

  const filtered = tests.filter((t) => {
    if (boardFilter !== "all" && t.board !== boardFilter) return false;
    if (gradeFilter !== "all" && String(t.grade) !== gradeFilter) return false;
    if (search && !t.topic?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="MCQ Tests — Practice Online | Ravi TestPapers"
        description="Free MCQ tests for Tamil Nadu State Board, CBSE and Competitive exams. Choose your class and topic, take the test and see your score instantly."
        canonical="/mcq"
      />

      {/* Hero header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white py-14 px-4">
        <MathSymbol symbol="π"  className="text-6xl top-4  left-[4%]"    delay={0}   duration={7} />
        <MathSymbol symbol="∑"  className="text-5xl top-8  right-[6%]"   delay={1.5} duration={8} />
        <MathSymbol symbol="√"  className="text-7xl bottom-4 left-[12%]" delay={0.8} duration={6} />
        <MathSymbol symbol="∫"  className="text-5xl top-6  left-[44%]"   delay={2}   duration={9} />
        <MathSymbol symbol="∞"  className="text-4xl bottom-6 right-[10%]" delay={1}  duration={7} />
        <MathSymbol symbol="x²" className="text-4xl top-1/3 left-[30%]"  delay={0.5} duration={8} />
        <MathSymbol symbol="÷"  className="text-5xl bottom-8 left-[55%]" delay={1.2} duration={7} />
        <MathSymbol symbol="="  className="text-5xl top-3  right-[22%]"  delay={0.3} duration={9} />

        <div className="relative max-w-screen-lg mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 border border-white/30 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            <HiClipboardList /> Online MCQ Tests
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mb-3">Practice MCQ Tests</h1>
          <p className="text-blue-100 text-sm sm:text-base max-w-lg mx-auto">
            Choose a test, answer the questions and get your score instantly — free for all students.
          </p>
        </div>
      </div>

      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-8">

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-4 mb-8 flex flex-col sm:flex-row gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 w-full">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
            <input
              type="text"
              placeholder="Search by topic…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          {isStudent ? (
            /* Student: show their class as a locked chip instead of dropdowns */
            <div className="flex items-center gap-2 shrink-0">
              <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 text-sm font-bold px-4 py-2 rounded-xl">
                <HiAcademicCap />
                Class {profile?.grade} · {BOARD_LABELS_FULL[profile?.board] || profile?.board}
              </span>
            </div>
          ) : (
            <>
              {/* Board */}
              <select value={boardFilter} onChange={(e) => setBoard(e.target.value)}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                <option value="all">All Boards</option>
                <option value="state">State Board</option>
                <option value="cbse">CBSE</option>
                <option value="competitive">Competitive</option>
              </select>
              {/* Grade */}
              <select value={gradeFilter} onChange={(e) => setGrade(e.target.value)}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                <option value="all">All Classes</option>
                {grades.map((g) => (
                  <option key={g} value={g}>Class {g}</option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Count */}
        <p className="text-gray-500 text-sm mb-5">
          <span className="font-bold text-gray-800">{filtered.length}</span> test{filtered.length !== 1 ? "s" : ""} found
        </p>

        {/* Cards */}
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <HiClipboardList className="text-6xl text-blue-200 mx-auto mb-4" />
            <p className="text-gray-400 font-semibold">No tests found</p>
            <p className="text-gray-300 text-sm mt-1">Try changing your filters or search term.</p>
          </div>
        ) : (
          <motion.div
            variants={stagger} initial="hidden" animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {filtered.map((test) => (
              <motion.article
                key={test.id}
                variants={fadeUp}
                className="bg-white rounded-2xl shadow-md hover:shadow-lg border border-blue-50 hover:border-blue-200 transition-all duration-200 flex flex-col overflow-hidden"
              >
                {/* Coloured top strip */}
                <div className="h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500" />

                <div className="p-5 flex flex-col gap-3 flex-1">
                  {/* Topic */}
                  <div className="flex items-start gap-2">
                    <h3 className="flex-1 font-black text-gray-900 text-base leading-snug line-clamp-2">
                      {test.topic || "Untitled Test"}
                    </h3>
                    <SaveButton kind="test" item={test} className="-mt-1.5 -mr-1.5" />
                  </div>

                  {/* Chips */}
                  <div className="flex flex-wrap gap-2">
                    {test.board && (
                      <span className={`inline-flex items-center gap-1 border text-xs font-semibold px-2.5 py-1 rounded-full ${BOARD_COLORS[test.board] || "bg-gray-50 text-gray-600 border-gray-100"}`}>
                        {test.board === "state" ? <HiAcademicCap className="text-xs" /> : test.board === "cbse" ? <HiBookOpen className="text-xs" /> : <HiLightningBolt className="text-xs" />}
                        {BOARD_LABELS[test.board] || test.board}
                      </span>
                    )}
                    {test.board === "competitive" && test.examType && (
                      <span className="inline-flex items-center bg-sky-100 text-sky-700 border border-sky-200 text-xs font-bold px-2.5 py-1 rounded-full">
                        {test.examType}
                      </span>
                    )}
                    {test.grade && (
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-100 text-xs font-semibold px-2.5 py-1 rounded-full">
                        <HiAcademicCap className="text-xs" /> Class {test.grade}
                      </span>
                    )}
                    {test.questionCount > 0 && (
                      <span className="inline-flex items-center bg-indigo-100 text-indigo-700 text-xs font-bold px-2.5 py-1 rounded-full">
                        {test.questionCount} Qs
                      </span>
                    )}
                  </div>

                  {/* Ref ID */}
                  {test.refId && (
                    <p className="text-[10px] font-mono text-gray-400 tracking-wider">#{test.refId}</p>
                  )}

                  {/* CTA */}
                  <Link
                    to={`/mcq/${test.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-xl text-sm transition-colors"
                  >
                    Start Test →
                  </Link>
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
